import { LinearOptimizationType } from '@blisspointmedia/bpm-types/dist/OptimizationConstraints';
import './LinearOptimizations.scss';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import * as Dfns from 'date-fns/fp';
import { capitalize } from 'inflected';
import { Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  MdAdd,
  MdAttachMoney,
  MdContentCopy,
  MdDelete,
  MdDirectionsCar,
  MdPeople,
  MdPlayArrow,
  MdWarning
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { AlertDialog } from '@Components/AlertDialog/AlertDialog';
import { Button } from '@Components/Button';
import { ConstraintFormMode, ConstraintFormModal } from '@Components/ConstraintFormModal';
import { DatePicker } from '@Components/DatePicker';
import {
  Appearance,
  Background,
  Design,
  Intent,
  Placement,
  Size
} from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { IconButton } from '@Components/IconButton';
import { Page } from '@Components/Page/Page';
import { BPMTable, TypedHeader } from '@Components/StickyTable';
import { TableLoading } from '@Components/TableEmptyState';
import { WidgetContainer } from '@Components/WidgetContainer';
import { formatOptimizationStatus } from '@Components/OptimizationFailureDetails/constraint-diagnostics-utils';
import {
  useCopyConstraint,
  useDeleteConstraint,
  useNewConstraint,
  useOptimizationKpis,
  useOptimizations,
  useRunOptimization,
  useConstraintsList,
  useStageTwoJobsInitiated,
  useValidWeeks
} from './use-linear-optimizations-data';
import { LogsModal } from './LogsModal';
import { LogsTarget } from './logs-modal-constants';
import {
  ConstraintListElement,
  ConstraintModalMode,
  isOptimizationType,
  LinearOptimizationPageProps,
  Optimization,
  OPTIMIZATION_TYPE_LABELS,
  OptimizationType,
  ROUTES,
  StageTwoJobsStatus
} from './linear-optimizations-constants';
import { formatOptimizationDate, getConstraintNameError } from '../../utils/optimization-utils';
import { useSetError } from '../../redux/modals';

// yyyy-MM-dd, the Monday of the current ISO week -- validWeeks holds Monday-anchored week-start
// dates, so this must be the start of the *current* week (not literally today) or the current
// week's own Monday becomes unselectable every day except Monday itself.
const CUTOFF_DATE = Dfns.format('yyyy-MM-dd')(Dfns.startOfISOWeek(new Date()));

const OPTIMIZATION_TYPE_ICONS: Record<LinearOptimizationType, ReactElement> = {
  [OptimizationType.BUYING]: <MdAttachMoney />,
  [OptimizationType.TRAFFIC]: <MdDirectionsCar />,
  [OptimizationType.AUDIENCE]: <MdPeople />
};

// Audience deliberately excluded -- not a creatable optimization type from this page.
const OPTIMIZATION_TYPE_OPTIONS: Record<string, string> = {
  [OptimizationType.BUYING]: OPTIMIZATION_TYPE_LABELS[OptimizationType.BUYING],
  [OptimizationType.TRAFFIC]: OPTIMIZATION_TYPE_LABELS[OptimizationType.TRAFFIC]
};

const DeleteConstraintModal = ({
  deleteConstraintId,
  onHide,
  onSubmit
}: {
  deleteConstraintId: string;
  onHide: () => void;
  onSubmit: () => void;
}) => (
  <Modal show onHide={onHide} centered>
    <Modal.Header closeButton>
      <Modal.Title>Delete Constraint</Modal.Title>
    </Modal.Header>
    <Modal.Body>Delete {deleteConstraintId}? This cannot be undone.</Modal.Body>
    <Modal.Footer>
      <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onHide}>
        Cancel
      </Button>
      <Button design={Intent.ERROR} onClick={onSubmit}>
        Delete
      </Button>
    </Modal.Footer>
  </Modal>
);

interface RunOptimizationModalProps {
  name: string;
  optimizationType: LinearOptimizationType;
  week?: string;
  optimizationKpis?: string[];
  running: boolean;
  onHide: () => void;
  onSubmit: (params: { kpi: string; branch: string }) => void;
}

const RunOptimizationModal = ({
  name,
  optimizationType,
  week,
  optimizationKpis,
  running,
  onHide,
  onSubmit
}: RunOptimizationModalProps) => {
  const [branch, setBranch] = useState('v4');
  const [kpi, setKpi] = useState<string>();

  return (
    <Modal show onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Run Constraint</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group>
          <Form.Label>Name</Form.Label>
          <Form.Control value={name} plaintext readOnly tabIndex={-1} />
        </Form.Group>
        <Form.Group>
          <Form.Label>Optimization KPI</Form.Label>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            options={optimizationKpis ?? []}
            value={kpi}
            disabled={!optimizationKpis}
            onChange={setKpi}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Branch</Form.Label>
          <Form.Control value={branch} onChange={event => setBranch(event.target.value)} />
        </Form.Group>
        <Form.Group>
          <Form.Label>Optimization Type</Form.Label>
          <Form.Control
            value={OPTIMIZATION_TYPE_LABELS[optimizationType]}
            plaintext
            readOnly
            tabIndex={-1}
          />
        </Form.Group>
        {optimizationType === OptimizationType.TRAFFIC && (
          <Form.Group>
            <Form.Label>Allocation Week</Form.Label>
            <Form.Control value={week} plaintext readOnly tabIndex={-1} />
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onHide}>
          Cancel
        </Button>
        <Button
          design={Intent.SUCCESS}
          disabled={!branch.trim() || !kpi?.trim()}
          loading={running}
          onClick={() => {
            if (!kpi) return;
            onSubmit({ kpi, branch });
          }}
        >
          Run
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const OptimizationHome = ({ company }: LinearOptimizationPageProps) => {
  const navigate = useNavigate();
  const setError = useSetError();
  const { data: optimizations, error: optimizationsError } = useOptimizations(company);
  const { data: constraintsList, error: constraintsListError } = useConstraintsList(company);

  useEffect(() => {
    if (optimizationsError) {
      setError({
        message: `Failed to load optimizations: ${optimizationsError.message}`,
        reportError: optimizationsError
      });
    }
  }, [optimizationsError, setError]);

  useEffect(() => {
    if (constraintsListError) {
      setError({
        message: `Failed to load constraints: ${constraintsListError.message}`,
        reportError: constraintsListError
      });
    }
  }, [constraintsListError, setError]);
  const { data: validWeeks, error: validWeeksError } = useValidWeeks(company);
  const { data: optimizationKpis, error: optimizationKpisError } = useOptimizationKpis(company);
  const { data: stageTwoJobsInitiated, error: stageTwoJobsInitiatedError } =
    useStageTwoJobsInitiated(company);

  useEffect(() => {
    if (validWeeksError) {
      setError({
        message: `Failed to load valid weeks: ${validWeeksError.message}`,
        reportError: validWeeksError
      });
    }
  }, [validWeeksError, setError]);

  useEffect(() => {
    if (optimizationKpisError) {
      setError({
        message: `Failed to load optimization KPIs: ${optimizationKpisError.message}`,
        reportError: optimizationKpisError
      });
    }
  }, [optimizationKpisError, setError]);

  useEffect(() => {
    if (stageTwoJobsInitiatedError) {
      setError({
        message: `Failed to load Stage 2 jobs status: ${stageTwoJobsInitiatedError.message}`,
        reportError: stageTwoJobsInitiatedError
      });
    }
  }, [stageTwoJobsInitiatedError, setError]);

  const copyConstraint = useCopyConstraint(company);
  const newConstraint = useNewConstraint(company);
  const deleteConstraint = useDeleteConstraint(company);
  const runOptimization = useRunOptimization(company);

  const [constraintModal, setConstraintModal] = useState<
    | { mode: typeof ConstraintModalMode.NEW }
    | { mode: typeof ConstraintModalMode.COPY; source: ConstraintListElement }
    | { mode: typeof ConstraintModalMode.DELETE; name: string }
    | { mode: typeof ConstraintModalMode.RUN; source: ConstraintListElement }
  >();
  // Owned here (not inside the shared ConstraintFormModal) since Linear's traffic allocation is a
  // single week, unlike Streaming's date range -- the modal only owns name/optimizationType.
  const [constraintFormWeek, setConstraintFormWeek] = useState<string>();
  const [logsTarget, setLogsTarget] = useState<LogsTarget>();
  const [optimizationFilterBar, setOptimizationFilterBar] = useState<HTMLDivElement | null>(null);
  const [constraintFilterBar, setConstraintFilterBar] = useState<HTMLDivElement | null>(null);

  const optimizationHeaders: TypedHeader<Optimization>[] = useMemo(
    () => [
      {
        label: 'Number',
        name: 'build_number',
        width: 60,
        renderer: row => (
          <Button
            appearance={Appearance.EMPTY}
            size={Size.SM}
            design={row.status === 'SUCCESS' ? Design.BRAND : Design.NEUTRAL}
            tooltip={{
              text: row.status === 'SUCCESS' ? `Open Run ${row.build_number}` : 'View run details'
            }}
            onClick={() =>
              row.status === 'SUCCESS'
                ? navigate(`${ROUTES.RUNS.uri}/${row.company_id}_${row.build_number}`)
                : setLogsTarget({
                    kpi: row.company_id,
                    buildNumber: row.build_number,
                    status: row.status,
                    start: row.start,
                    end: row.end
                  })
            }
          >
            {row.build_number}
          </Button>
        )
      },
      {
        label: 'KPI',
        name: 'company_id',
        className: 'linear-optimization-kpi',
        flex: 1,
        minFlexWidth: 175,
        renderer: row => <span title={row.company_id}>{row.company_id}</span>
      },
      {
        label: 'Status',
        name: 'status',
        className: 'linear-optimization-status',
        flex: 1,
        minFlexWidth: 160,
        renderer: row => {
          const status = formatOptimizationStatus(row.status, row.progress);
          return <span title={status}>{status}</span>;
        }
      },
      {
        label: 'Date (Local Time)',
        name: 'date',
        width: 180,
        renderer: row => formatOptimizationDate(row.date)
      },
      { label: 'Constraint Name', name: 'constraint_name', width: 120 },
      {
        label: 'Branch',
        name: 'branch',
        className: 'linear-optimization-branch',
        width: 60,
        renderer: row => <span title={row.branch}>{row.branch}</span>
      },
      {
        label: 'OF',
        name: 'objective_value',
        width: 90,
        renderer: row => (row.objective_value != null ? Math.round(row.objective_value) : '')
      },
      {
        label: 'Cost',
        name: 'cost',
        width: 100,
        renderer: row => (row.cost != null ? `$${Math.round(row.cost).toLocaleString()}` : '')
      },
      {
        label: 'Budget',
        name: 'budget',
        width: 100,
        renderer: row => (row.budget != null ? `$${Math.round(row.budget).toLocaleString()}` : '')
      }
    ],
    [navigate]
  );

  const constraintHeaders: TypedHeader<ConstraintListElement>[] = useMemo(
    () => [
      {
        label: '',
        name: 'optimizationType',
        width: 30,
        nonInteractive: true,
        renderer: row => (
          <OverlayTrigger
            placement='left'
            overlay={
              <Tooltip id={`optimization-type-${row.name}`}>
                {capitalize(row.optimizationType)} optimization
              </Tooltip>
            }
          >
            <span className='optimization-type-icon'>
              {OPTIMIZATION_TYPE_ICONS[row.optimizationType]}
            </span>
          </OverlayTrigger>
        )
      },
      {
        label: 'Name',
        name: 'name',
        width: 150,
        renderer: row => (
          <Button
            appearance={Appearance.EMPTY}
            size={Size.SM}
            onClick={() => navigate(`${ROUTES.CONSTRAINTS.uri}/${row.name}`)}
          >
            {row.name}
          </Button>
        )
      },
      {
        label: 'Last Modified (Eastern)',
        name: 'lastmodified',
        flex: 1,
        minFlexWidth: 160,
        renderer: row => formatOptimizationDate(row.lastmodified)
      },
      {
        label: '',
        name: 'actions',
        width: 120,
        nonFilterable: true,
        renderer: row => (
          <div className='constraint-actions'>
            <IconButton
              appearance={Appearance.OUTLINED}
              design={Design.NEUTRAL}
              size={Size.SM}
              aria-label={`Run ${row.name}`}
              tooltip={{ text: 'Run optimization' }}
              onClick={() => setConstraintModal({ mode: ConstraintModalMode.RUN, source: row })}
            >
              <MdPlayArrow />
            </IconButton>
            <IconButton
              appearance={Appearance.OUTLINED}
              design={Design.NEUTRAL}
              size={Size.SM}
              aria-label={`Copy ${row.name}`}
              tooltip={{ text: 'Copy constraint' }}
              onClick={() => {
                setConstraintFormWeek(row.week);
                setConstraintModal({ mode: ConstraintModalMode.COPY, source: row });
              }}
            >
              <MdContentCopy />
            </IconButton>
            <IconButton
              appearance={Appearance.OUTLINED}
              design={Intent.ERROR}
              size={Size.SM}
              aria-label={`Delete ${row.name}`}
              tooltip={{ text: 'Delete constraint' }}
              onClick={() =>
                setConstraintModal({ mode: ConstraintModalMode.DELETE, name: row.name })
              }
            >
              <MdDelete />
            </IconButton>
          </div>
        )
      }
    ],
    [navigate]
  );

  const renderOptimizationsLoading = useCallback(
    () => (
      <TableLoading
        isLoading={!optimizations && !optimizationsError}
        error={optimizationsError}
        rowHeight={32}
        headerHeight={32}
      />
    ),
    [optimizations, optimizationsError]
  );

  const renderConstraintsLoading = useCallback(
    () => (
      <TableLoading
        isLoading={!constraintsList && !constraintsListError}
        error={constraintsListError}
        rowHeight={36}
        headerHeight={32}
      />
    ),
    [constraintsList, constraintsListError]
  );

  return (
    <Page
      title='Linear Optimizations'
      pageType='Linear Optimizations'
      minHeight='600px'
      isLoading={!optimizations || !constraintsList}
    >
      <div className='linear-optimizations-home'>
        <WidgetContainer
          background={Background.DARK}
          className='optimization-runs-widget'
          header={
            <>
              <h2 className='table-name'>Optimizations</h2>
              <div className='widget-filter-bar-container' ref={setOptimizationFilterBar} />
            </>
          }
        >
          <BPMTable
            autoHeight
            className='optimization-table'
            headerHeight={32}
            rowHeight={32}
            data={optimizations ?? []}
            defaultAdvancedFilter
            externalFilterBarContainer={optimizationFilterBar}
            headers={optimizationHeaders}
            noRowsRenderer={renderOptimizationsLoading}
          />
        </WidgetContainer>
        <WidgetContainer
          background={Background.DARK}
          className='constraints-widget'
          header={
            <>
              <h2 className='table-name'>Constraints</h2>
              <div className='widget-filter-bar-container' ref={setConstraintFilterBar} />
            </>
          }
          rightActions={
            <IconButton
              appearance={Appearance.OUTLINED}
              size={Size.LG}
              aria-label='New constraint'
              tooltip={{ text: 'New constraint' }}
              onClick={() => {
                setConstraintFormWeek(undefined);
                setConstraintModal({ mode: ConstraintModalMode.NEW });
              }}
            >
              <MdAdd />
            </IconButton>
          }
        >
          <BPMTable
            autoHeight
            className='constraint-list-table'
            headerHeight={32}
            rowHeight={36}
            data={constraintsList ?? []}
            externalFilterBarContainer={constraintFilterBar}
            headers={constraintHeaders}
            noRowsRenderer={renderConstraintsLoading}
          />
        </WidgetContainer>
      </div>

      {constraintModal?.mode === ConstraintModalMode.NEW && (
        <ConstraintFormModal
          mode={ConstraintFormMode.NEW}
          optimizationTypeOptions={OPTIMIZATION_TYPE_OPTIONS}
          initialOptimizationType={OptimizationType.BUYING}
          getNameError={getConstraintNameError}
          isValid={(_name, optimizationType) =>
            optimizationType !== OptimizationType.TRAFFIC || Boolean(constraintFormWeek)
          }
          renderExtraFields={optimizationType =>
            optimizationType === OptimizationType.TRAFFIC && (
              <Form.Group>
                <Form.Label>Allocation Week</Form.Label>
                <DatePicker
                  appearance={Appearance.OUTLINED}
                  isSingle
                  fullWeeksOnly
                  range={
                    constraintFormWeek
                      ? { start: constraintFormWeek, end: constraintFormWeek }
                      : undefined
                  }
                  background={Background.LIGHT}
                  design={Design.BRAND}
                  isOutsideRange={date => date < CUTOFF_DATE || !(validWeeks ?? []).includes(date)}
                  onChange={({ start }) => setConstraintFormWeek(start)}
                  removeHeaderText
                />
              </Form.Group>
            )
          }
          onHide={() => {
            setConstraintModal(undefined);
            setConstraintFormWeek(undefined);
          }}
          onSubmit={({ name, optimizationType }) => {
            if (!isOptimizationType(optimizationType)) return;
            setConstraintModal(undefined);
            setConstraintFormWeek(undefined);
            newConstraint.mutate({
              to: name,
              optimizationType,
              week: optimizationType === OptimizationType.TRAFFIC ? constraintFormWeek : undefined
            });
          }}
        />
      )}
      {constraintModal?.mode === ConstraintModalMode.COPY && (
        <ConstraintFormModal
          mode={ConstraintFormMode.COPY}
          sourceName={constraintModal.source.name}
          optimizationTypeOptions={OPTIMIZATION_TYPE_OPTIONS}
          initialOptimizationType={constraintModal.source.optimizationType}
          disableOptimizationType
          getNameError={getConstraintNameError}
          isValid={(_name, optimizationType) =>
            optimizationType !== OptimizationType.TRAFFIC || Boolean(constraintFormWeek)
          }
          renderExtraFields={optimizationType =>
            optimizationType === OptimizationType.TRAFFIC && (
              <Form.Group>
                <Form.Label>Allocation Week</Form.Label>
                <DatePicker
                  appearance={Appearance.OUTLINED}
                  isSingle
                  fullWeeksOnly
                  range={
                    constraintFormWeek
                      ? { start: constraintFormWeek, end: constraintFormWeek }
                      : undefined
                  }
                  background={Background.LIGHT}
                  design={Design.BRAND}
                  isOutsideRange={date => date < CUTOFF_DATE || !(validWeeks ?? []).includes(date)}
                  onChange={({ start }) => setConstraintFormWeek(start)}
                  removeHeaderText
                />
              </Form.Group>
            )
          }
          onHide={() => {
            setConstraintModal(undefined);
            setConstraintFormWeek(undefined);
          }}
          onSubmit={({ name, optimizationType }) => {
            if (!isOptimizationType(optimizationType)) return;
            setConstraintModal(undefined);
            setConstraintFormWeek(undefined);
            copyConstraint.mutate({
              from: constraintModal.source.name,
              to: name,
              optimizationType,
              week: optimizationType === OptimizationType.TRAFFIC ? constraintFormWeek : undefined
            });
          }}
        />
      )}
      {constraintModal?.mode === ConstraintModalMode.DELETE && (
        <DeleteConstraintModal
          deleteConstraintId={constraintModal.name}
          onHide={() => setConstraintModal(undefined)}
          onSubmit={() => {
            setConstraintModal(undefined);
            deleteConstraint.mutate({ deleteConstraintId: constraintModal.name });
          }}
        />
      )}
      {constraintModal?.mode === ConstraintModalMode.RUN && (
        <RunOptimizationModal
          name={constraintModal.source.name}
          optimizationType={constraintModal.source.optimizationType}
          week={constraintModal.source.week}
          optimizationKpis={optimizationKpis}
          running={runOptimization.isPending}
          onHide={() => setConstraintModal(undefined)}
          onSubmit={({ kpi, branch }) => {
            const { name, optimizationType, week } = constraintModal.source;
            setConstraintModal(undefined);
            runOptimization.mutate({ name, kpi, branch, optimizationType, week });
          }}
        />
      )}
      {logsTarget && (
        <LogsModal target={logsTarget} company={company} onHide={() => setLogsTarget(undefined)} />
      )}

      <AlertDialog
        show={
          stageTwoJobsInitiated?.status === StageTwoJobsStatus.DISABLED ||
          stageTwoJobsInitiated?.status === StageTwoJobsStatus.MISSING_MLDIAGNOSTICS
        }
        onHide={() => {}}
        escapable={false}
        intent={Intent.WARNING}
        icon={<MdWarning size={48} />}
        title={
          stageTwoJobsInitiated?.status === StageTwoJobsStatus.DISABLED
            ? 'Linear Stage 2 Jobs Not Enabled'
            : 'KPI Mismatch or Job Failure'
        }
        body={
          stageTwoJobsInitiated?.status === StageTwoJobsStatus.DISABLED ? (
            'Please reach out to engineering in order to enable and run Stage 2 jobs for this company.'
          ) : (
            <>
              <p>
                The ML diagnostics file is missing for the default KPI:{' '}
                <strong>{stageTwoJobsInitiated?.kpi}</strong>
              </p>
              <p>This could indicate either:</p>
              <ul>
                <li>The default KPI is incorrectly configured</li>
                <li>The Linear Stage 2 job has failed for the default KPI</li>
              </ul>
              <p>Please verify the KPI configuration or check job execution logs.</p>
            </>
          )
        }
        showFooter={false}
      />
    </Page>
  );
};
